import { Box, BoxProps, createStyles, makeStyles, useMediaQuery } from '@material-ui/core'
import { OrganizationSignupMailRequest } from '@noco/http-client/lib/noco'
import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Link, Button, Checkbox, Colors, Typography } from 'src/components/atoms'
import { useCreateOrganization } from 'src/fixtures/modules/organization/hooks'
import * as yup from 'yup'
import { FormRow } from './FormRow'
import { PRIVACY_LINK, TERMS_LINK } from 'src/fixtures/utils/constant'
import Image from 'next/image'
import theme from 'src/styles/theme'

export interface FormProps {
  // MEMO: email はメールを送信した先
  onSuccessSubmit?: (email: string) => void
}

type FormValue = OrganizationSignupMailRequest

const styles: { [key in string]: BoxProps['sx'] } = {
  formRow: { display: 'flex', alignItems: 'flex-start', mb: '24px' }
}

const useStyles = makeStyles(() =>
  createStyles({
    button: {
      padding: '0'
    }
  })
)

export const createOrganizationValidationSchema = yup.object().shape({
  encryptedEmail: yup
    .string()
    .email('有効なメールアドレスを入力してください')
    .label('メールアドレス')
    .required('メールアドレスは必須です')
})

export const Form = ({ onSuccessSubmit }: FormProps) => {
  const classes = useStyles()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { handleCreateOrganizationSignupMail, isLoading, error } = useCreateOrganization()
  const [isConcent, setIsConsent] = useState(false)
  const formik = useFormik<FormValue>({
    isInitialValid: false,
    validationSchema: createOrganizationValidationSchema,
    initialValues: {
      encryptedEmail: ''
    },
    onSubmit: values => {
      handleCreateOrganizationSignupMail(values)
      setIsSubmitting(true)
    }
  })

  useEffect(() => {
    if (isSubmitting && !isLoading && !error) {
      setIsSubmitting(false)
      onSuccessSubmit?.(formik.values.encryptedEmail!)
      formik.resetForm()
    }
  }, [isLoading, error, isSubmitting, formik, onSuccessSubmit])

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box color={Colors.base.black} mb="35px">
        <Box position="relative" width={isDownSm ? '99px' : '114px'} height={isDownSm ? '29px' : '33px'}>
          <Image alt="" src="/images/nocosell_logo.svg" layout="fill" objectFit="contain" />
        </Box>
        <Box mt="40px" />
        <Typography variant="h3" fontSize={isDownSm ? 'm' : 'l'} fontWeight="bold" align="center">
          1分でかんたん登録
        </Typography>
        <Box mt="4px" />
        <Typography variant="h2" fontSize={isDownSm ? 'l' : 'xl'} fontWeight="bold" align="center">
          無料でアカウントを作成しよう
        </Typography>
        <Box mt="44px" />
        <Box sx={styles.formRow}>
          <FormRow
            label="メールアドレス"
            placeholder="ビジネスでご利用のメールアドレス"
            name="encryptedEmail"
            type="encryptedEmail"
            value={formik.values.encryptedEmail}
            onChange={formik.handleChange}
            onBlur={e => {
              formik.handleBlur(e)
              formik.setFieldValue('encryptedEmail', formik.values.encryptedEmail?.trim())
            }}
            required
            fullWidth
            large
            error={formik.touched.encryptedEmail ? formik.errors.encryptedEmail : undefined}
          />
        </Box>
        <Box display={'flex'} alignItems="center" marginLeft="-8px">
          <Checkbox value={isConcent} onChange={() => setIsConsent(v => !v)} />

          <Box>
            <Typography fontSize="s">
              <a
                href={TERMS_LINK}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'none', color: Colors.accent.keyBlue.default }}
              >
                利用規約
              </a>
              および
              <a
                href={PRIVACY_LINK}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'none', color: Colors.accent.keyBlue.default }}
              >
                プライバシーポリシー
              </a>
              に同意する
            </Typography>
          </Box>
        </Box>
        <Box mt="20px" />
        <Button
          title="無料でアカウントを作成する"
          size="large"
          width="100%"
          type="submit"
          disabled={!formik.isValid || !isConcent}
          className={classes.button}
        />
        <Box mt="24px" />
        <Typography fontSize="s" align="center">
          既にアカウントをお持ちの方は&nbsp;
          <Link text="こちら" url="/signin" />
        </Typography>
      </Box>
    </form>
  )
}
